import React, { memo, useEffect, useRef, useState } from "react";
import { RCCDropDownItem, RCCGrid, RCCHookToast, RCCIcon, RCCServicesApi } from "react-common-components";
import CheckPermission from "src/components/CheckPermission";
import OptionsColumn from "src/components/DefaultColumnsList/Options";
import UserColumn from "src/components/DefaultColumnsList/User";
import List from "src/components/List";
import { useAuth } from "src/context/Auth/context";
import { PermissionEnum } from "src/context/Auth/interface";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import formatDate from "src/utils/formatDate";
import { IAnalysis, IRefModalAnalysis } from "../../interface";
import BadgeAnalysis from "../BadgeAnalysis";
import ModalRiskAnalysis from "../modalAnalysis";
import { IListAnalysis, TypeBadge } from "./interface";

const ListAnalysis: React.FC<IListAnalysis> = ({
	riskData,
	onCallback
}) => {
	const {translate, currentLanguage} = useTranslation();
	const {confirm} = useConfirmModal();
	const {addToast} = RCCHookToast();
	const { administrator, superUser, user, checkIfHasSpecificPermission } = useAuth();

	const [analysis, setAnalysis] = useState<IAnalysis[]>([]);
	const [analysisEdit, setAnalysisEdit] = useState<number>();

	const refModalAnalysis = useRef<IRefModalAnalysis>(null);

	useEffect(() => {
		if(riskData.ritbb003.length > 0)
		{
			setAnalysis(riskData.ritbb003);
			setAnalysisEdit(riskData.ritbb003[0].rib003Id);
		}
	}, [riskData.ritbb003]);

	const deleteRiskAnalysis = async(id: number) => {
		confirm(
			translate("Exclusão de Análise de Risco"),
			`${translate("Deseja realmente excluir a Análise de Risco")}?`,
			async () => {
				await RCCServicesApi.deleteRequest({
					method: "RisksAnalysis/Delete",
					urlParameters: {
						rib001Id: riskData.rib001Id,
						rib003Id: id					
					}
				});

				const newAnalysis = analysis.filter((item) => item.rib003Id !== id);
				setAnalysisEdit(newAnalysis.length > 0 ? newAnalysis[0].rib003Id : 0);
				setAnalysis(newAnalysis);
				
				if(onCallback)
					onCallback();

				addToast({
					color: "green",
					description: translate("Análise de Risco excluída com sucesso"),
					title: translate("Sucesso")
				});
			},
			(err: any) => {
				standardErrorMessages(err, translate("Erro ao excluir a Análise de Risco"), addToast);
			},
			true
		);
	};

	const changeRiskAnalysis = (analiseEdit: IAnalysis) => {
		setAnalysis((prevState) => prevState.map((analise) => {
			if(analise.rib003Id === analiseEdit.rib003Id){
				return {
					...analise,
					rib003Id: analiseEdit.rib003Id,
					rib003Descricao: analiseEdit.rib003Descricao,
					rib003Impacto: analiseEdit.rib003Impacto,
					rib003Probabilidade: analiseEdit.rib003Probabilidade,
					rib003Ria002Titulo: analiseEdit.rib003Ria002Titulo,
					rib003Ria002Id: analiseEdit.rib003Ria002Id
				};
			}

			return analise;
		}));

		if(onCallback)
			onCallback();
	};

	const colUser = (row: any) => (
		<UserColumn 
			name={row.eftba005.efa005Nome}
			image={row.eftba005.efa005Imagem}
		/>
	);

	const colDescription = (row: any) => (
		<RCCGrid
			container
			item
			alignItems="center"
			justifyContent="space-between"
		>
			<span title={row.rib003Descricao}>{row.rib003Descricao}</span>
		</RCCGrid>
	);

	const colProbabilidade = (row: any) => (
		<BadgeAnalysis 
			value={row.rib003Probabilidade}
			type={TypeBadge.PROBABILITY}
		/>
	);

	const colEstrategia = (row: any) => (
		<>
			<RCCGrid 
			>
				{translate(`${row.rib003Ria002Titulo}`)}
			</RCCGrid>
		</>		
	);

	const colImpacto = (row: any) => (
		<>
			<BadgeAnalysis 
				value={row.rib003Impacto}
				type={TypeBadge.IMPACT}
			/>
		</>	
	);

	const colData = (row : any) => (
	  <>
		  {formatDate(row.rib003Data, currentLanguage)}
	  </>
	);

	const options = (row: any) => {

		const isAdminOrSuperOrAutority = administrator || superUser || row.rib003Efa005Id === user.efa005Id;
		const canCreateUpdate = checkIfHasSpecificPermission("/risks/panel", PermissionEnum.CREATE_UPDATE) && riskData.userPermission === 2;
		const lineEdit = analysisEdit === row.rib003Id;
		const multipleLines = analysis.length > 1;

		return (	
			<>
				{
					lineEdit && (isAdminOrSuperOrAutority || canCreateUpdate) &&
					<RCCGrid 
						container
						item
						styleInline="justify-content: flex-end"
					>
						<OptionsColumn>
							<RCCDropDownItem
								selfEnclose
								onClick={() => { refModalAnalysis.current?.actionButtonChange(riskData, row.rib003Id); }}
							>
								<RCCIcon name="pencil"/>
								 {translate("Alterar")}
							</RCCDropDownItem>

							{multipleLines && (isAdminOrSuperOrAutority || row.userPermission === 2) && (
								<CheckPermission path="/risks/panel" minimumPermissionRequired={PermissionEnum.CREATE_UPDATE_DELETE}>
									<RCCDropDownItem 
										selfEnclose
										onClick={() => { deleteRiskAnalysis(row.rib003Id); }}
									>
										<RCCIcon name="trash"/>
										{translate("Excluir")}
									</RCCDropDownItem>
								</CheckPermission>
							)}
						</OptionsColumn>
					</RCCGrid>
				}
			</>		
		);
	};
    
	return(
		<>
			<List 
				data={analysis}
				columns={[
					{
						idColumn: "rib001Efa005Nome",
						nameColumn: translate("Responsável"),
						xs: 12,
						sm: 4,
						justify: "left",
						cellRenderer: colUser
					},
					{
						idColumn: "rib003Data",
						nameColumn: translate("Data"),
						xs: 12,
						sm: 1,
						justify: "center",
						cellRenderer: colData
					},
					{
						idColumn: "rib003Probabilidade",
						nameColumn: translate("Probabilidade"),
						xs: 12,
						sm: 2,
						justify: "center",
						cellRenderer: colProbabilidade
					},
					{
						idColumn: "rib001Impacto",
						nameColumn: translate("Impacto"),
						xs: 12,
						sm: 1,
						justify: "center",
						currency: "",
						cellRenderer: colImpacto
					},
					{
						idColumn: "rib003Ria002Titulo",
						nameColumn: translate("Estratégia"),
						xs: 12,
						sm: 3,
						justify: "center",
						cellRenderer: colEstrategia
					},
					{
						idColumn: "options",
						nameColumn: "",
						xs: 12,
						sm: 1,
						justify: "right",
						cellRenderer: options
					},
					{
						idColumn: "ribb001Titulo",
						nameColumn: translate("Análise"),
						justify: "left",
						cellRenderer: colDescription,
						xs: 12
					}
				]}
				showHeader
			/>

			<ModalRiskAnalysis onCallbackUpdate={changeRiskAnalysis} ref={refModalAnalysis}/>
		</>
	);};

export default memo(ListAnalysis);
